import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  Snackbar
} from '@mui/material';
import { investmentAPI } from '../../api/investmentAPI';
import MarketAnalysisWorkflow from '../investment/MarketAnalysisWorkflow';
import Watchlists from '../investment/Watchlists';
import Screeners from '../investment/Screeners';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProfile } from '../../contexts/ProfileContext';
import { useLoadingStatus } from '../../contexts/LoadingStatusContext';

// Simplified PremiumContent component
const PremiumContent = ({
  urlPrimaryBase,
  marketReportFull,
  marketReportLoading,
  marketReportError,
}) => {
  // Get loading status functions for API
  const { addStatus, removeStatus } = useLoadingStatus();
  // Create API instance with loading functions
  const api = useMemo(() => investmentAPI(addStatus, removeStatus), []);
  const navigate = useNavigate();
  const location = useLocation();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  // Add state to track navigation needs
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // Get URL parameters
  const searchParams = useMemo(() => {
      console.log('PremiumC (Simplified): in useMemo - URL Params:', location.search);
      return new URLSearchParams(location.search);
    }, [location.search]); // Use useMemo
  const tab = searchParams.get('tab');
  const profileId = searchParams.get('profileId');
  const profileSection = searchParams.get('profileSection') || 'analysis';

  // Get profile context
  const {currentProfile} = useProfile();

  // Handle navigation as a side effect
  useEffect(() => {
    if (pendingNavigation) {
      navigate(
        `${location.pathname}?${pendingNavigation.params.toString()}`,
        { replace: pendingNavigation.replace }
      );
      setPendingNavigation(null);
    }
  }, [pendingNavigation, navigate, location.pathname]);

  // Effect for analysis view defaults
  useEffect(() => {
    if (profileSection === 'analysis' && !searchParams.has('view') && currentProfile?.id) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('view', 'dashboard');
      setPendingNavigation({ params: newParams, replace: true });
    }
  }, [profileSection, searchParams, currentProfile?.id]);

  // Effect for handling default profile section
  useEffect(() => {
    if (currentProfile?.id && profileSection !== 'analysis' && 
        profileSection !== 'watchlists' && profileSection !== 'screeners') {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('profileSection', 'analysis');
      if (!newParams.has('view')) {
        newParams.set('view', 'dashboard');
      }
      setPendingNavigation({ params: newParams, replace: true });
    }
  }, [profileSection, searchParams, currentProfile?.id]);

  // --- Navigation Helper ---
  const navigateToTicker = useCallback((tickerToView) => {
    const params = new URLSearchParams(location.search);
    params.set('view', 'ticker');
    params.set('ticker', tickerToView);
    // Preserve existing params like tab, profileId, profileSection if needed
    navigate(`${location.pathname}?${params.toString()}`);
  }, [navigate, location]);


  const handleAddToDashboard = async (widget) => {
    let statusId = null;
    try {
      if (!currentProfile?.id) {
        setSnackbar({ open: true, message: 'No profile selected', severity: 'error' });
        return;
      }
      
      statusId = addStatus(`Adding widget to dashboard...`);
      const response = await api.addDashboardWidget({
        ...widget,
        profileId: currentProfile.id,
        position: { x: 0, y: 0 }
      });
      setSnackbar({ open: true, message: 'Added to dashboard successfully', severity: 'success' });
    } catch (error) {
      console.error('Failed to add widget to dashboard:', error);
      setSnackbar({ open: true, message: error.message || 'Failed to add to dashboard', severity: 'error' });
    } finally {
      if (statusId !== null) {
        removeStatus(statusId);
      }
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // --- Rendering Logic ---

  // Function to render content based on profile section (when tab=profiles)
  const renderProfileSectionContent = () => {
    console.log('Rendering profile section:', profileSection);
    switch (profileSection) {
      case 'analysis':
        console.log('Rendering MarketAnalysisWorkflow component');
        // Check if view param is missing, but don't navigate directly
        if (!searchParams.has('view')) {
          return <Box sx={{ p: 2, textAlign: 'center' }}>Loading dashboard view...</Box>;
        }
        return (
          <MarketAnalysisWorkflow
            profileId={currentProfile.id}
            profile={currentProfile}
            key={currentProfile.id} // Ensure re-render on profile change
            onTickerSelect={navigateToTicker} // Use navigation callback
            marketReportFull={marketReportFull} // Pass down the prop
            isLoading={marketReportLoading} // Pass down loading state
            error={marketReportError} // Pass down error state
          />
        );
      case 'watchlists':
        console.log('Rendering Watchlists component');
        return (
          <Watchlists
            profileId={currentProfile.id}
            profile={currentProfile}
            urlBase={urlPrimaryBase}
            onAddToDashboard={handleAddToDashboard}
            onTickerSelect={navigateToTicker} // Add navigation callback
          />
        );
      case 'screeners':
        console.log('Rendering Screeners component');
        return (
          <Screeners
            profileId={currentProfile.id}
            profile={currentProfile}
            urlBase={urlPrimaryBase}
            onAddToDashboard={handleAddToDashboard}
            onTickerSelect={navigateToTicker} // Add navigation callback
          />
        );
      default:
        console.log('No matching profile section found, defaulting to analysis');
        // Return a loading indicator while waiting for redirect via useEffect
        return <Box sx={{ p: 2, textAlign: 'center' }}>Redirecting to analysis dashboard...</Box>;
    }
  };


  // Main component return logic
  return (
    <Box sx={{ textAlign: 'left', maxWidth: 1800, mx: 'auto', p: 1}}>
      {!currentProfile ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Please select an investment profile to continue
          </Typography>
        </Box>
      ) : (
        <>
          {/* Decide whether to show Profile Section or Overview */}
          {renderProfileSectionContent()}

          {/* Snackbar remains for general feedback */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
          >
            <Alert
              onClose={handleSnackbarClose}
              severity={snackbar.severity}
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </>
      )}
    </Box>
  );
}

export default PremiumContent;
